import { isScopeRequired } from "./scope";

/**
 * Raised by a data source whose scope is complete (tenants, period…) but that has
 * nothing to show for it: no feed is configured for the tenant, or the feed has
 * never received observations. Unlike a failure, retrying changes nothing; unlike
 * {@link ScopeRequiredError}, the viewer has nothing left to choose.
 */
export class DataUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DataUnavailableError";
  }
}

export function isDataUnavailable(error: unknown): boolean {
  return error instanceof Error && error.name === "DataUnavailableError";
}

/**
 * The common reasons a widget has no data to draw, shown the same way by every widget:
 *
 * - `scope`: a choice is still missing (tenants, period, report edition). Neutral.
 * - `unavailable`: the scope is complete but the source has nothing for it. Neutral.
 * - `error`: the request failed. Offers Retry.
 *
 * A successful response without rows is not an issue: it is the widget's own empty state.
 */
export type WidgetDataIssueKind = "scope" | "unavailable" | "error";

export interface WidgetDataIssue {
  kind: WidgetDataIssueKind;
  /** What the source says about it, in words a viewer can act on (`scope`, `unavailable`). */
  reason?: string;
  retry?: () => void;
  retrying?: boolean;
}

/** Results kept on screen while a newer request failed: a discreet header notice. */
export interface WidgetDataNotice {
  /** `stale`: the whole refresh failed; `partial`: some of the data could not be loaded. */
  kind: "stale" | "partial";
  /** When the results on screen were received (epoch ms). */
  updatedAt?: number;
  retry?: () => void;
  retrying?: boolean;
}

/** The one wording shared by every widget for the common issues. */
export const WIDGET_ISSUE_TEXT = {
  scope: "Nothing to show yet",
  unavailable: "No data available",
  error: "Data could not be loaded",
  errorReason: "Try again in a moment.",
  retry: "Retry",
  retrying: "Retrying…",
  stale: "Not updated",
  staleDetail: "The latest update failed; showing the last available results.",
  partial: "Partial data",
  partialDetail: "Some data could not be loaded; showing the available results.",
} as const;

export function dataIssueKind(error: unknown): WidgetDataIssueKind {
  if (isScopeRequired(error)) return "scope";
  if (isDataUnavailable(error)) return "unavailable";
  return "error";
}

/** Whether an error says something the viewer should read, rather than a technical failure. */
export function isExpectedDataIssue(error: unknown): boolean {
  return dataIssueKind(error) !== "error";
}

/** The issue an error stands for; only scope and availability keep the source's words. */
export function toDataIssue(error: Error, retry?: () => void, retrying?: boolean): WidgetDataIssue {
  const kind = dataIssueKind(error);
  return kind === "error"
    ? { kind, retry, retrying }
    : { kind, reason: error.message || undefined };
}
